import { X, Check, Trash2 } from "lucide-react";
import useGeneralNotification from "../../components/patients/hooks/useNotifcation";
import { useAuth } from "../../context/AuthContext";
import { useEffect, useRef } from "react";

interface NotificationPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

export function NotificationPanel({ isOpen, onClose }: NotificationPanelProps) {
  if (!isOpen) return null;
  const { user } = useAuth();
  const { notifications, updateNotificationStatus, deleteNotifications } =
    useGeneralNotification({
      toId: user?.id || "",
      notify: false,
    });

  const formatTime = (timestamp: { seconds: number; nanoseconds: number }) => {
    const date = new Date(timestamp?.seconds * 1000);
    return new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(date);
  };

  // Get appropriate color based on order type
  const getNotificationColor = (orderType: string) => {
    switch (orderType) {
      case "With Prescription":
        return "bg-purple-50 border-purple-200";
      case "Without Prescription":
        return "bg-blue-50 border-blue-200";
      case "Assigned order":
        return "bg-green-50 border-green-200";
      case "Direct Order":
        return "bg-amber-50 border-amber-200";
      default:
        return "bg-gray-50 border-gray-200";
    }
  };

  const handleMarkAsRead = (notificationId: string) => {
    updateNotificationStatus(String(notificationId));
  };

  const handleDelete = (notificationId: string) => {
    deleteNotifications(String(notificationId));
  };

  return (
    <div className="fixed inset-y-0 right-0 max-w-md w-full bg-white shadow-xl z-50 overflow-y-auto">
      <div className="p-4 border-b border-gray-200 flex justify-between items-center bg-gray-50">
        <h2 className="text-xl font-semibold text-gray-800">Notifications</h2>
        <button
          onClick={onClose}
          className="p-1 rounded-full hover:bg-gray-200 transition-colors"
        >
          <X className="w-5 h-5 text-gray-500" />
        </button>
      </div>

      <div className="divide-y divide-gray-100">
        {notifications.length === 0 ? (
          <div className="p-6 text-center text-gray-500">
            No notifications available
          </div>
        ) : (
          notifications.map((notification) => (
            <div
              key={notification?.id}
              className={`p-4 border-l-4 ${getNotificationColor(
                notification?.orderType
              )} ${notification?.status === "Unread" ? "font-medium" : ""}`}
            >
              <div className="flex justify-between items-start mb-2">
                <div>
                  <h3
                    className={`${
                      notification?.status === "Unread"
                        ? "text-gray-900"
                        : "text-gray-700"
                    }`}
                  >
                    {notification?.title}
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">
                    {notification?.message}
                  </p>
                </div>
                <span
                  className={`text-xs px-2 py-1 rounded-full ${
                    notification?.status === "Unread"
                      ? "bg-blue-100 text-blue-800"
                      : "bg-gray-100 text-gray-800"
                  }`}
                >
                  {notification?.status}
                </span>
              </div>

              {/* Order metadata */}
              <div className="mt-2 space-y-1">
                {notification?.orderNumber && (
                  <p className="text-xs text-gray-500">
                    Order #: {notification?.orderNumber}
                  </p>
                )}
                {notification?.orderType && (
                  <p className="text-xs capitalize text-gray-500">
                    Type:{" "}
                    {notification?.orderType.replace(/([A-Z])/g, " $1").trim()}
                  </p>
                )}
                {notification?.orderDate && (
                  <p className="text-xs text-gray-500">
                    Date:{" "}
                    {new Date(notification?.orderDate).toLocaleDateString()}
                  </p>
                )}
              </div>

              <div className="flex justify-between items-center mt-3">
                {/* <span className="text-xs text-gray-400">
                  {formatTime(notification?.timestamp)}
                </span> */}
                <div className="flex gap-2">
                  <button
                    onClick={() => handleMarkAsRead(notification?.id)}
                    disabled={notification?.status === "Read"}
                    className={`p-2 rounded-full ${
                      notification?.status === "Unread"
                        ? "bg-blue-100 text-blue-600 hover:bg-blue-200"
                        : "bg-gray-100 text-gray-400 cursor-default"
                    }`}
                    title="Mark as read"
                  >
                    <Check className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(notification?.id)}
                    className="p-2 rounded-full bg-red-100 text-red-600 hover:bg-red-200"
                    title="Delete notification"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
